import {
  clearWalletconnectV2Storage,
  generateSingingMessage,
  isMobileDevice,
  toRoninWalletDeepLink,
} from '@account-service/core'
import type { InitialData } from '@roninnetwork/wallet-sdk'
import { WCEvent } from '@roninnetwork/wallet-sdk'
import { useCallback, useEffect, useState } from 'react'

import type { ErrorResponse } from '~/apis'
import { connectRoninAPI, disconnectRoninAPI, getNonceAPI } from '~/apis'
import { EventName, sendEvent } from '~/apis/tracking'
import {
  CLIENT_META,
  defaultChainId,
  ERRORS,
  walletConnectProjectId,
} from '~/constants'
import { useSentry } from '~/core/sentry'
import { useAuthContext } from '~/hooks/useAuthContext'
import { useCheckOnMobileWallet } from '~/hooks/useCheckOnMobileWallet'
import { useModal } from '~/hooks/useModal'
import { useWrappedToast } from '~/hooks/useWrappedToast'
import { WalletSDK } from '~/utils/WalletSDK'

import type { TConnectProps } from '../Connect'

export enum ErrorRoninWallet {
  EMPTY = '',
  UNLOCKED = 'WalletIsLocked',
  NOTFOUND = 'NotInstalled',
  WRONG_ADDRESS = 'wrong_address',
  LINK = 'ERR_RONIN_ADDRESS_IS_BLOCKED_TO_LINK_OTHER_ACCOUNT',
  UNLINK = 'ERR_ACCOUNT_BLOCKED_TO_UNLINK',
}

const sdk = new WalletSDK({
  mobileOptions: {
    walletConnectProjectId,
    defaultChainId,
  },
  dappMetadata: CLIENT_META,
})

const useHooks = (props: TConnectProps) => {
  const isMobile = isMobileDevice()
  const {
    isVisible: isVisiblePreConnectModal,
    openModal: openPreConnectModal,
    closeModal: closePreConnectModal,
  } = useModal()
  const [universalLink, setUniversalLink] = useState('')
  const { connected } = props
  const { showError, showSuccessMessage } = useWrappedToast()
  const { getProfile, userV2 } = useAuthContext()
  const { captureException } = useSentry()
  const [isLoading, setIsLoading] = useState(false)
  const [errorRonin, setErrorRonin] = useState<ErrorRoninWallet>(
    ErrorRoninWallet.EMPTY,
  )
  const [blockedTime, setBlockedTime] = useState('')

  const mpcAddress = userV2?.account?.wallet?.identity

  const isInWalletApp = useCheckOnMobileWallet()

  useEffect(() => {
    if (isMobile)
      sdk?.on(WCEvent.DISPLAY_URI, uri => {
        setUniversalLink(toRoninWalletDeepLink(uri))
      })
  }, [isMobile])

  useEffect(() => {
    if (universalLink) {
      openPreConnectModal()
    }
  }, [isVisiblePreConnectModal, openPreConnectModal, universalLink])

  const resetPreConnectWallet = () => {
    closePreConnectModal()
    setUniversalLink('')
  }

  const handleConnect = useCallback(async () => {
    setIsLoading(true)

    try {
      // Note: To clear session
      await sdk.disconnect()
      clearWalletconnectV2Storage()

      let connectResult: InitialData | undefined

      if (isMobile && !isInWalletApp) {
        connectResult = await sdk.connectMobile()
      } else {
        connectResult = await sdk.connectInjected()
      }

      const address = connectResult.accounts[0]
      const provider = sdk.getProvider()
      const { chainId } = connectResult

      if (!provider || !address || !chainId) return

      const { nonce, issued_at, not_before, expiration_time } =
        await getNonceAPI(address)

      const message = generateSingingMessage({
        address,
        chainId,
        nonce,
        issuedAt: issued_at,
        notBefore: not_before,
        expirationTime: expiration_time,
      })

      const signature = (await provider.request({
        method: 'personal_sign',
        params: [message, address],
      })) as string

      if (!connected) {
        if (mpcAddress?.toLowerCase() === address.toLowerCase()) {
          showError(
            'It is your keyless wallet address. Please switch to another address',
          )
          return
        }

        await connectRoninAPI({
          message,
          address,
          signature,
        })
      } else {
        await disconnectRoninAPI({
          message,
          address,
          signature,
        })
      }

      await getProfile()
      showSuccessMessage(
        `${!connected ? 'Link' : 'Unlink'} ronin wallet successfully`,
      )

      const eventTracking = props.connected
        ? EventName.UNLINK_WALLET
        : EventName.LINK_WALLET

      sendEvent({
        type: 'track',
        data: {
          event: eventTracking,
          action: eventTracking,
          ref: eventTracking,
        },
      })
    } catch (error) {
      const message = (error as Error)?.message
      if (message === ErrorRoninWallet.NOTFOUND) {
        setErrorRonin(ErrorRoninWallet.NOTFOUND)
        window.open('https://wallet.roninchain.com/', '_blank')
        return
      }
      if (message === ErrorRoninWallet.UNLOCKED) {
        setErrorRonin(ErrorRoninWallet.UNLOCKED)
        return
      }

      const { error_status, error_message, error_details } =
        error as ErrorResponse

      if (error_status === 'ERR_RONIN_DOES_NOT_MATCH') {
        setErrorRonin(ErrorRoninWallet.WRONG_ADDRESS)
        return
      }

      if (
        error_status === ErrorRoninWallet.LINK ||
        error_status === ErrorRoninWallet.UNLINK
      ) {
        const blockedTime = error_details as {
          ExpiresIn: string
        }
        setErrorRonin(error_status)
        setBlockedTime(blockedTime.ExpiresIn)

        return
      }

      if (
        [
          'ERR_RONIN_OAUTH_TOKEN_INVALID',
          'ERR_UNLINK_RONIN_ADDRESS_WITH_WEB3AUTH',
          'ERR_LINK_WITH_EXIST_PROFILE',
          'ERR_ACCOUNT_CONNECTED_WITH_OTHER_EMAIL',
          'ERR_RONIN_ADDRESS_IS_BLOCKED_TO_LINK_OTHER_ACCOUNT_IN_24H',
        ].includes(error_status)
      ) {
        showError(ERRORS[error_status])
        return
      }

      if (error_message) {
        showError(error_message)
        return
      }

      captureException(error)
    } finally {
      setIsLoading(false)
    }
  }, [
    isMobile,
    isInWalletApp,
    connected,
    getProfile,
    showSuccessMessage,
    props.connected,
    mpcAddress,
    showError,
    captureException,
  ])

  return {
    isLoading,
    errorRonin,
    blockedTime,
    setErrorRonin,
    handleConnect,
    universalLink,
    isVisiblePreConnectModal,
    resetPreConnectWallet,
  }
}

export default useHooks
